/* eslint-disable no-prototype-builtins */
/* eslint-disable no-plusplus */
/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { rfpNameSchema } from './RfpNameSchema';
import { excelRichTextToHtml } from './HtmlRichTextProcessor';
// import maliciousPDFCheck from './malicious-pdf-check';
import { allowedDocsTypes, allowedFileTypes, allowedImgTypes } from 'src/utils/fileType';
import DummyCompanyLogo from 'src/asserts/images/png/Dummy_Company_logo.jpg';
import { PaperProps, Paper } from '@mui/material';
import { useRef, useState, useEffect } from 'react';
import Draggable from 'react-draggable';
import { usePrompt } from 'src/hooks/usePrompt';
export const getUniqueListBy = (arr, key) => {
  return [...new Map(arr.map((item) => [item[key], item])).values()];
};

// Safely extracts plain text from an ExcelJS cell value. Cells written as rich
// text (HTML comments on export) come back as `{ richText: [...] }`, hyperlinks
// as `{ text, hyperlink }` and formulas as `{ formula, result }` — calling
// `.toString()` on those yields "[object Object]". This normalises all of them
// to a plain string so importing never surfaces "[object Object]".
export const getCellText = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    if (Array.isArray((value as any).richText)) {
      return (value as any).richText.map((part: any) => part?.text ?? '').join('');
    }
    if (typeof (value as any).text === 'string') return (value as any).text;
    if ('result' in value) return getCellText((value as any).result);
    if ((value as any).hyperlink) return String((value as any).hyperlink);
    if (value instanceof Date) return value.toISOString();
  }
  return String(value);
};

// Like getCellText, but for comment cells: rich-text cells (exported from HTML
// comments) are reconstructed back into HTML so the rich-text editor renders
// their bold/italic/underline/colour formatting. Plain/string cells fall back to
// getCellText unchanged.
export const getCellHtml = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (Array.isArray((value as any).richText)) return excelRichTextToHtml((value as any).richText);
    if ('result' in value) return getCellHtml((value as any).result);
  }
  return getCellText(value);
};

// Trims an HTML string so its visible text length is at most maxChars, cutting
// from the END while keeping tags valid. Character counting matches TipTap's
// CharacterCount (textSize mode): each text character counts, each <br> counts
// as 1, block boundaries count as 0. Used so over-limit imported content (e.g.
// USP, capped at 300) loads fully into the capped editor instead of the editor
// trimming from the start and dropping the heading.
export const truncateHtmlByTextLength = (html: string, maxChars: number): string => {
  if (!html || typeof html !== 'string' || maxChars < 0) return html;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const textLength = (doc.body.textContent || '').length + doc.body.querySelectorAll('br').length;
  if (textLength <= maxChars) return html;

  let remaining = maxChars;
  let done = false;
  const walk = (node: Node) => {
    const children = Array.from(node.childNodes);
    for (const child of children) {
      if (done) {
        child.parentNode?.removeChild(child);
        continue;
      }
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent || '';
        if (text.length <= remaining) {
          remaining -= text.length;
        } else {
          child.textContent = text.slice(0, remaining);
          remaining = 0;
          done = true;
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        if (el.tagName === 'BR') {
          if (remaining <= 0) {
            el.parentNode?.removeChild(el);
            done = true;
          } else {
            remaining -= 1;
          }
        } else {
          walk(el);
        }
      }
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
};

export const formatNumber = (number) => {
  // Check if the number has a decimal part
  if (number % 1 !== 0) {
    // If it has a decimal part, format it with two decimal places
    return number.toFixed(2);
  } else {
    // If it's an integer, display it without decimal places
    return number.toFixed(0);
  }
};

export const isValidURL = (url) => {
  var pattern = new RegExp('^https?:\\/\\/(www\\.)?([\\w-]+\\.)+[\\w]{2,}(\\/[\\w-]+)*\\/?.*$');
  return pattern.test(url);
};

export const getLastTwoDigitsAfterDecimal = (number) => {
  // Convert the number to a string
  if (!number) {
    return;
  }
  const numStr = number.toString();

  // Check if the number has a decimal point
  const decimalIndex = numStr.indexOf('.');
  if (decimalIndex === -1) {
    // If there's no decimal point, return the number as-is
    return number;
  }

  // Extract the last two characters after the decimal point
  const lastTwoDigits = numStr.slice(decimalIndex + 1, decimalIndex + 3);

  // Convert the last two digits back to a number
  const result = parseFloat(`${numStr.slice(0, decimalIndex)}.${lastTwoDigits}`);

  return result;
};

export const addDotsIfMoreThan = (str, limit) => {
  if (str?.length > limit) {
    return str?.slice(0, limit) + '...';
  }
  return str;
};
export const addDotsIfMoreThan15 = (str) => {
  return addDotsIfMoreThan(str, 15);
};
export const addDotsIfMoreThan10 = (str) => {
  return addDotsIfMoreThan(str, 10);
};

export const addDotsIfMoreThan20 = (str) => {
  return addDotsIfMoreThan(str, 20);
};

export const addDotsIfMoreThanValue = (str, value) => {
  return addDotsIfMoreThan(str, value);
};

export const addDotsIfMoreThan50 = (str) => {
  return addDotsIfMoreThan(str, 50);
};

export const addDotsIfMoreThan40 = (str) => {
  return addDotsIfMoreThan(str, 40);
};

export const isValidJwtFormat = (token) => {
  // Regular expression to match a JWT format
  const jwtRegex = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/;
  return jwtRegex.test(token);
};

export const formatToDDMMYYYY = (dateString) => {
  const date = moment(dateString, 'ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');
  return date.isValid() ? date.format('DD/MM/YYYY') : undefined;
};

export const addSpacesBeforeUppercase = (str) => {
  return str
    .split('')
    .map((char, index) => {
      if (index !== 0 && char === char.toUpperCase()) {
        return ` ${char}`;
      }
      return char;
    })
    .join('');
};

export const parseAcceptanceValue = (value: any): boolean => {
  if (!value) return false;
  const stringValue = value.toString().toLowerCase();
  return stringValue === 'yes' || stringValue === 'true' ? true : false;
};

export const handleAmountValue = (value: string): string | number => {
  return !isNaN(Number(value)) && value !== '' ? Number(value) : '';
};

export const downloadAsExcel = async (data, rangeDate, sendNotification) => {
  const workbook = new ExcelJS.Workbook();
  const workSheetName = 'Worksheet-1';
  try {
    const workbooks = workbook.addWorksheet(workSheetName);
    workbooks.getColumn(1).width = 50;
    workbooks.getColumn(2).width = 50;
    const addedRows = [];

    const addConditionalRow = (condition: any, rowNumber, values, alignment) => {
      const isEmpty =
        condition === undefined ||
        condition === '' ||
        (Array.isArray(condition) && condition?.length === 0) ||
        condition.fromDate === '' ||
        condition.toDate === '';
      if (!isEmpty) {
        const row = workbooks.getRow(rowNumber);
        row.height = 30;
        row.values = values;
        row.alignment = alignment;
        addedRows.push(rowNumber);
      }
    };
    addedRows?.forEach((rowNumber) => {
      const row = workbooks.getRow(rowNumber);
      if (Array.isArray(row.values) && row.values.every((cell) => cell === null)) {
        workbooks.spliceRows(rowNumber, 1);
      }
    });

    addConditionalRow(rangeDate, 2, ['Selected Date Range', formatToDDMMYYYY(rangeDate?.fromDate) + ' - ' + formatToDDMMYYYY(rangeDate?.toDate)], {
      vertical: 'middle',
      horizontal: 'center'
    });

    const headerRow = workbooks.getRow(3);
    headerRow.values = [
      'Company',
      'Awaiting Response',
      'Drafting',
      'Conflicted',
      'Did Not Bid',
      'Submitted',
      'Lost',
      'Won',
      'Aborted',
      'Ended',
      'Seeking Approval for Waiver'
    ];
    headerRow.font = { bold: true };
    headerRow.height = 30;
    headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

    data?.forEach((dataEntry) => {
      const dataRow = workbooks.addRow([]);
      dataRow.values = Object.values<any>(dataEntry).map((value) => (value === undefined || value === '' ? 0 : value));
      dataRow.eachCell({ includeEmpty: false }, (cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
    });

    workbooks.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell({ includeEmpty: false }, (cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });
    const buf = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buf]), 'InvitationsAndOutcomes.xlsx');
    sendNotification({ message: 'Excel exported successfully.', variant: 'success' });
  } catch (error) {
    console.error('Something Went Wrong', error.message);
  } finally {
    workbook.removeWorksheet(workSheetName);
  }
};

export const downloadAsPdf = (id, TableId) => {
  const input = document.getElementById(id);
  html2canvas(input).then((canvas) => {
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF();

    pdf.addImage({
      imageData: imgData,
      format: 'PNG',
      x: 5,
      y: 5,
      width: 200,
      height: 130
    });
    pdf.addPage();
    const startYForTable = 10;
    autoTable(pdf, {
      html: `#${TableId}`,
      margin: { top: startYForTable, bottom: 10, left: 10, right: 10 },
      theme: 'grid',
      styles: {
        cellPadding: 3,
        fontSize: 10,
        valign: 'middle'
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: 50,
        minCellHeight: 0,
        fontStyle: 'bold'
      },
      bodyStyles: {
        minCellHeight: 15
      },
      didDrawCell: (data) => {
        if (data.row.section === 'head' && data.column.index === 1) {
          pdf.setDrawColor(190, 190, 191);
          pdf.setLineWidth(0.2);
          let finalY = data.cell.y + data.cell.height;
          pdf.line(data.cell.x, data.cell.y, data.cell.x, finalY);
        }
      }
    });
    pdf.save('InvitationOutcomes.pdf');
  });
};

export const validateExcelFileStructure = (workbook, requiredSheets, requiredColumns) => {
  const workbookSheetNames = workbook.worksheets.map((sheet) => sheet.name.trim());

  const allSheetsPresent = requiredSheets.every((requiredSheet) => workbookSheetNames.includes(requiredSheet.trim()));
  if (!allSheetsPresent) {
    return { isValid: false, message: 'Excel file is missing required sheets.' };
  }

  for (const sheetName of requiredSheets) {
    const sheet = workbook.getWorksheet(sheetName);
    if (!sheet) {
      return { isValid: false, message: `Required sheet "${sheetName}" is missing.` };
    }
    let requiredColumnsFound = false;
    sheet.eachRow((row) => {
      const rowValues = row.values.map((cell) => cell && cell.toString().trim().toLowerCase());
      if (rowValues && requiredColumns) {
        requiredColumnsFound = true;
      }
    });

    if (!requiredColumnsFound) {
      return { isValid: false, message: `Required columns are missing in sheet "${sheetName}".` };
    }
  }

  return { isValid: true, message: 'Excel file structure is valid.' };
};

export const getNestedProperty = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj);
};

export const setNestedProperty = (obj, path, value) => {
  const parts = path.split('.');
  const lastPart = parts.pop();
  const lastObj = parts.reduce((acc, part) => {
    if (!acc[part]) acc[part] = {};
    return acc[part];
  }, obj);
  lastObj[lastPart] = value;
};

const removeDuplicatesAndEmptyRows = (dataArray) => {
  const uniqueRows = [];
  const uniqueRowsSet = new Set();

  dataArray.forEach((row) => {
    const stringifiedRow = JSON.stringify(row);
    if (!uniqueRowsSet.has(stringifiedRow) && row?.name !== '') {
      if (row?.nestedValue && (row?.name === row?.nestedValue?.name || row?.question === row?.nestedValue?.question)) {
        return;
      }
      uniqueRowsSet.add(stringifiedRow);
      uniqueRows.push(row);
    }
  });

  return uniqueRows;
};

export const removeDuplicatesAndEmptyRowsFromNestedData = (jsonData) => {
  // Iterate over each key in the jsonData object
  Object.keys(jsonData).forEach((key) => {
    // Check if the value is an array
    if (Array.isArray(jsonData[key])) {
      jsonData[key] = removeDuplicatesAndEmptyRows(jsonData[key]);
    } else if (typeof jsonData[key] === 'object') {
      jsonData[key] = removeDuplicatesAndEmptyRowsFromNestedData(jsonData[key]);
    }
  });

  return jsonData;
};

function updateObjectField(object, questionPath, acceptance, comment) {
  const path = questionPath.split('.').filter(Boolean);
  let currentObject = object;
  for (let i = 0; i < path?.length; i++) {
    const key = path[i];
    if (currentObject[key] !== undefined) {
      if (i === path?.length - 1) {
        // Special case for additional_information - use 'value' instead of 'noted_accepted'
        if (key === 'additional_information') {
          currentObject[key].value = acceptance;
          currentObject[key].comment = comment;
        } else {
          currentObject[key].noted_accepted = acceptance;
          currentObject[key].comment = comment;
        }
        return;
      }
      currentObject = currentObject[key];
    } else {
      console.error('Path not found:', questionPath);
      return;
    }
  }
}

export const updateBasedOnTemplateItem = (object, templateItem) => {
  if (templateItem && templateItem.question) {
    updateObjectField(object, templateItem.question, templateItem.acceptance, templateItem.comment);
  }
  if (templateItem.nestedValue && templateItem.nestedValue?.length > 0) {
    templateItem.nestedValue.forEach((nestedItem) => {
      const nestedQuestionPath = templateItem.question ? `${templateItem.question}.${nestedItem.question}` : nestedItem.question;
      updateObjectField(object, nestedQuestionPath, nestedItem.acceptance, nestedItem.comment);
    });
  }
};

const pricingByIndex = {
  1: 'name',
  2: 'question',
  3: 'acceptance',
  4: 'comment'
};
export const processRowForAcceptancePricing = (row, pricingRowData) => {
  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (cell.worksheet._name === 'Pricing') {
      const cellValue = getCellText(cell.value).toLowerCase();
      const pricingCellValues = getCellText(cell?._row?._cells[0].value).toLowerCase();
      if (
        pricingCellValues === 'Currency' ||
        pricingCellValues === 'No travel expected' ||
        pricingCellValues === 'Tax advice is excluded' ||
        pricingCellValues === 'Power Point slides' ||
        pricingCellValues === 'Following Jurisdiction/Language explicitly excluded from scope of work'
      ) {
        pricingRowData = {
          name: '',
          question: '',
          acceptance: false,
          comment: ''
        };
        row.eachCell({ includeEmpty: false }, (cellVal, index) => {
          const key = pricingByIndex[index];
          if (!key) return;
          if (key === 'comment') {
            pricingRowData.comment = getCellHtml(cellVal.value);
          } else if (key === 'acceptance') {
            pricingRowData.acceptance = parseAcceptanceValue(cellVal.value);
          } else {
            pricingRowData[key] = getCellText(cellVal.value);
          }
        });
      } else {
        if (colNumber <= 4) {
          switch (colNumber) {
            case 1:
              pricingRowData.name = getCellText(cell.value);
              break;
            case 2:
              pricingRowData.question = getCellText(cell.value);
              break;
            case 3:
              pricingRowData.acceptance = parseAcceptanceValue(cell.value);
              // pricingRowData.acceptance = cell.value === 'yes' || cell.value === 'Yes' || cell.value === 'YES' ? true : false;
              break;
            case 4:
              pricingRowData.comment = getCellHtml(cell.value);
              break;
          }
        } else {
          let index = Math.floor((colNumber - 5) / 4);
          let propertyIndex = (colNumber - 5) % 4;
          let value = getCellText(cell.value);
          if (!Array.isArray(pricingRowData.nestedValue)) {
            pricingRowData.nestedValue = [];
          }

          if (typeof pricingRowData.nestedValue[index] === 'undefined') {
            pricingRowData.nestedValue[index] = {
              name: '',
              question: '',
              acceptance: false,
              comment: '',
              amount: ''
            };
          }
          switch (propertyIndex) {
            case 0:
              pricingRowData.nestedValue[index].name = value;
              break;
            case 1:
              pricingRowData.nestedValue[index].question = value;
              break;
            case 2:
              pricingRowData.nestedValue[index].acceptance = parseAcceptanceValue(value);
              // pricingRowData.nestedValue[index].acceptance = value === 'yes' || value === 'Yes' || value === 'YES' ? true : false;
              break;
            case 3:
              pricingRowData.nestedValue[index].comment = getCellHtml(cell.value);
              break;
            case 4:
              pricingRowData.nestedValue[index].comment = getCellHtml(cell.value);
              break;
            case 5:
              pricingRowData.nestedValue[index].amount = handleAmountValue(value);
              break;
          }
        }
      }
    }
  });
};

export const processRowForEstimate = (row, pricingRowData) => {
  let colProcessed = 0;

  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (cell.worksheet._name === 'Pricing') {
      if (colNumber <= 4) {
        switch (colNumber) {
          case 1:
            pricingRowData.name = getCellText(cell.value);
            break;
          case 2:
            pricingRowData.question = getCellText(cell.value);
            break;
          case 3:
            pricingRowData.amount = handleAmountValue(cell.value);
            break;
          case 4:
            pricingRowData.comment = getCellHtml(cell.value);
            break;
        }
        colProcessed = colNumber;
      } else {
        let newIndex = Math.floor((colNumber - colProcessed - 1) / 2);
        pricingRowData.nestedValue = pricingRowData.nestedValue || [];
        pricingRowData.nestedValue[newIndex] = pricingRowData.nestedValue[newIndex] || { description: '', description_amount: '' };
        if (typeof pricingRowData.nestedValue[newIndex] === 'undefined') {
          pricingRowData.nestedValue[newIndex] = {
            description: '',
            description_amount: ''
          };
        }

        if ((colNumber - colProcessed - 1) % 2 === 0) {
          pricingRowData.nestedValue[newIndex].description = getCellText(cell.value);
        } else {
          pricingRowData.nestedValue[newIndex].description_amount = handleAmountValue(cell.value);
        }
      }
    }
  });
};
export const processRowForFourConceptAlternativePricing = (row, pricingRowData) => {
  let colProcessed = 0;

  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (cell.worksheet._name === 'Pricing') {
      if (colNumber <= 4) {
        switch (colNumber) {
          case 1:
            pricingRowData.name = getCellText(cell.value);
            break;
          case 2:
            pricingRowData.question = getCellText(cell.value);
            break;
          case 3:
            pricingRowData.acceptance = parseAcceptanceValue(cell.value);
            break;
          case 4:
            pricingRowData.comment = getCellHtml(cell.value);
            break;
        }
        colProcessed = colNumber;
      }
      //  else {
      //   let newIndex = Math.floor((colNumber - colProcessed - 1) / 2);
      //   pricingRowData.nestedValue = pricingRowData.nestedValue || [];
      //   pricingRowData.nestedValue[newIndex] = pricingRowData.nestedValue[newIndex] || { description: '', description_amount: '' };
      //   if (typeof pricingRowData.nestedValue[newIndex] === 'undefined') {
      //     pricingRowData.nestedValue[newIndex] = {
      //       description: '',
      //       description_amount: ''
      //     };
      //   }

      //   if ((colNumber - colProcessed - 1) % 2 === 0) {
      //     pricingRowData.nestedValue[newIndex].description = getCellText(cell.value);
      //   } else {
      //     pricingRowData.nestedValue[newIndex].description_amount = getCellText(cell.value);
      //   }
      // }
    }
  });
};
export const processRowForTwoConceptAlternativePricing = (row, pricingRowData) => {
  let colProcessed = 0;

  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (cell.worksheet._name === 'Pricing') {
      if (colNumber <= 4) {
        switch (colNumber) {
          case 1:
            pricingRowData.name = getCellText(cell.value);
            break;
          case 2:
            pricingRowData.question = getCellText(cell.value);
            break;
          case 3:
            pricingRowData.amount = handleAmountValue(cell.value);
            break;
          case 4:
            pricingRowData.comment = getCellHtml(cell.value);
            break;
        }
        colProcessed = colNumber;
      }
      //  else {
      //   let newIndex = Math.floor((colNumber - colProcessed - 1) / 2);
      //   pricingRowData.nestedValue = pricingRowData.nestedValue || [];
      //   pricingRowData.nestedValue[newIndex] = pricingRowData.nestedValue[newIndex] || { description: '', description_amount: '' };
      //   if (typeof pricingRowData.nestedValue[newIndex] === 'undefined') {
      //     pricingRowData.nestedValue[newIndex] = {
      //       description: '',
      //       description_amount: ''
      //     };
      //   }

      //   if ((colNumber - colProcessed - 1) % 2 === 0) {
      //     pricingRowData.nestedValue[newIndex].description = getCellText(cell.value);
      //   } else {
      //     pricingRowData.nestedValue[newIndex].description_amount = getCellText(cell.value);
      //   }
      // }
    }
  });
};
export const processRowForBlendedPricing = (row, pricingRowData) => {
  let colProcessed = 0;

  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (cell.worksheet._name === 'Pricing') {
      if (colNumber <= 3) {
        switch (colNumber) {
          case 1:
            pricingRowData.name = getCellText(cell.value);
            break;
          case 2:
            pricingRowData.question = getCellText(cell.value);
            break;
          case 3:
            pricingRowData.comment = getCellHtml(cell.value);
            break;
        }
        colProcessed = colNumber;
      } else {
        let newIndex = Math.floor((colNumber - colProcessed - 1) / 2);
        pricingRowData.nestedValue = pricingRowData.nestedValue || [];
        pricingRowData.nestedValue[newIndex] = pricingRowData.nestedValue[newIndex] || { description: '', description_amount: '' };
        if (typeof pricingRowData.nestedValue[newIndex] === 'undefined') {
          pricingRowData.nestedValue[newIndex] = {
            description: '',
            description_amount: ''
          };
        }

        if ((colNumber - colProcessed - 1) % 2 === 0) {
          pricingRowData.nestedValue[newIndex].description = getCellText(cell.value);
        } else {
          pricingRowData.nestedValue[newIndex].description_amount = handleAmountValue(cell.value);
        }
      }
    }
  });
};
export const processRowForTotalFee = (row, pricingRowData) => {
  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (cell.worksheet._name === 'Pricing') {
      if (colNumber) {
        switch (colNumber) {
          case 1:
            pricingRowData.name = getCellText(cell.value);
            break;
          case 2:
            pricingRowData.question = getCellText(cell.value);
            break;
          case 3:
            pricingRowData.amount = cell.value !== null && cell.value !== undefined && cell.value !== '' ? String(cell.value) : '';
            break;
          case 4:
            pricingRowData.description = getCellHtml(cell.value);
            break;
        }
      }
    }
  });
};
export const processRowForAltCheckBoxComponents = (row, pricingRowData) => {
  let colProcessed = 0;

  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (cell.worksheet._name === 'Pricing') {
      if (colNumber <= 4) {
        switch (colNumber) {
          case 1:
            pricingRowData.name = getCellText(cell.value);
            break;
          case 2:
            pricingRowData.question = getCellText(cell.value);
            break;
          case 3:
            pricingRowData.amount = handleAmountValue(cell.value);
            break;
          case 4:
            pricingRowData.comment = getCellHtml(cell.value);
            break;
        }
        colProcessed = colNumber;
      } else {
        let newIndex = Math.floor((colNumber - colProcessed - 1) / 2);
        pricingRowData.nestedValue = pricingRowData.nestedValue || [];
        pricingRowData.nestedValue[newIndex] = pricingRowData.nestedValue[newIndex] || { description: '', description_amount: '' };
        if (typeof pricingRowData.nestedValue[newIndex] === 'undefined') {
          pricingRowData.nestedValue[newIndex] = {
            description: '',
            description_amount: ''
          };
        }

        if ((colNumber - colProcessed - 1) % 2 === 0) {
          pricingRowData.nestedValue[newIndex].description = getCellText(cell.value);
        } else {
          pricingRowData.nestedValue[newIndex].description_amount = handleAmountValue(cell.value);
        }
      }
    }
  });
};

export const processRowForOtherPricing = (row, pricingRowData) => {
  row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
    if (cell.worksheet._name === 'Pricing') {
      if (colNumber <= 4) {
        switch (colNumber) {
          case 1:
            pricingRowData.name = getCellText(cell.value);
            break;
          case 2:
            pricingRowData.question = getCellText(cell.value);
            break;
          case 3:
            pricingRowData.acceptance = cell.value;
            break;
          case 4:
            pricingRowData.comment = getCellHtml(cell.value);
            break;
        }
      } else {
        let index = Math.floor((colNumber - 5) / 4);
        pricingRowData.nestedValue = pricingRowData.nestedValue || [];
        pricingRowData.nestedValue[index] = pricingRowData.nestedValue[index] || {
          comment: '',
          question: '',
          acceptance: false,
          name: '',
          keys: {}
        };

        let value = getCellText(cell.value);
        switch ((colNumber - 5) % 4) {
          case 0:
            pricingRowData.nestedValue[index].name = value;
            pricingRowData.nestedValue[index].keys = rfpNameSchema?.find((data) => data?.label?.toLowerCase() === value?.toLowerCase());
            break;
          case 1:
            pricingRowData.nestedValue[index].question = value;
            break;
          case 2:
            pricingRowData.nestedValue[index].acceptance = value;
            break;
          case 3:
            pricingRowData.nestedValue[index].comment = getCellHtml(cell.value);
            break;
        }
      }
    }
  });
};

export const updatePricingModelWithServerData = (newProposalInitialValues, serverItem) => {
  let updatedNestedValues;
  let modelKey = serverItem.name.toLowerCase().replace(/ /g, '_').replace('for_project', '').replace('up_to_a_certain_amount', '');
  let model = newProposalInitialValues.pricing[0].model[modelKey];
  if (model) {
    let detailsArray = [];
    if (
      serverItem.name === 'Blended Fee' ||
      serverItem.name === 'Contingent Fee' ||
      serverItem.name === 'Abort Discount' ||
      serverItem.name === 'Success Fee' ||
      serverItem.name === 'Budget for project' ||
      serverItem.name === 'Other' ||
      serverItem.name === 'Bidders to indicate any applicable tax to fees and %' ||
      serverItem.name === 'Free initial advice up to a certain amount'
    ) {
      const description = serverItem.description || '';
      const amount = serverItem.description_amount || null;
      detailsArray.push({ description, amount });

      if (serverItem.nestedValue && serverItem.nestedValue.length > 0) {
        serverItem.nestedValue.slice(1).forEach((nestedItem) => {
          const nestedDescription = nestedItem.description || '';
          const nestedAmount = handleAmountValue(nestedItem.amount);
          detailsArray.push({ description: nestedDescription, amount: nestedAmount });
        });
      }
      if (model.alternative_or_additional_pricing_benefits) {
        model.alternative_or_additional_pricing_benefits.details = detailsArray;
      } else {
        model.details = detailsArray;
      }
    } else {
      if (serverItem.nestedValue && serverItem.nestedValue.length > 0) {
        detailsArray = serverItem.nestedValue
          .filter((nv) => nv.description !== '' || nv.amount.toString() !== '')
          .slice(0, 1)
          .map((nv) => ({
            description: nv.description || nv.comment || '',
            amount: handleAmountValue(nv.amount) || nv.comment || null
          }));

        if (model.alternative_or_additional_pricing_benefits) {
          model.alternative_or_additional_pricing_benefits.details = detailsArray;
        } else {
          model.details = detailsArray;
        }
      }
    }
    // Update the 'value' flag based on the presence of details
    const details = model.alternative_or_additional_pricing_benefits ? model.alternative_or_additional_pricing_benefits.details : model.details;
    if (details?.length > 0) {
      if (model.alternative_or_additional_pricing_benefits) {
        model.alternative_or_additional_pricing_benefits.value = true;
      }
    } else if (model.alternative_or_additional_pricing_benefits) {
      model.alternative_or_additional_pricing_benefits.value = false;
    }
  }
};

export const updatePricingAlternativeBenefits = (newProposalInitialValues, serverItem) => {
  let modelKey = serverItem.question;
  let model = newProposalInitialValues.pricing[0].model[modelKey];
  if (model) {
    let detailsArray = [];
    if (serverItem.nestedValue && serverItem.nestedValue.length > 0) {
      detailsArray = serverItem?.nestedValue
        .filter((nv) => nv?.description !== '' || nv?.amount?.toString() !== '')
        .slice(0, 5)
        .map((nv) => ({
          description: nv.description || '',
          amount: handleAmountValue(nv.description_amount)
        }));
      if (model.breakdown_phases) {
        model.breakdown_phases = detailsArray;
      } else {
        model.details = detailsArray;
      }
    }
    // const details = model.alternative_or_additional_pricing_benefits ? model.alternative_or_additional_pricing_benefits.details : model.details;
    // if (details?.length > 0) {
    //   if (model.alternative_or_additional_pricing_benefits) {
    //     model.alternative_or_additional_pricing_benefits.value = true;
    //   }
    // } else if (model.alternative_or_additional_pricing_benefits) {
    //   model.alternative_or_additional_pricing_benefits.value = false;
    // }
  }
};

export const updateJsonDataWithArrayKeys = (jsonData, dataFormat) => {
  const updatedJsonData = JSON.parse(JSON.stringify(jsonData));
  const updateSection = (section) => {
    section.forEach((item) => {
      const matchedDataArrayItem = dataFormat.find((data) => data.label === item.name);
      if (matchedDataArrayItem) {
        item.question = matchedDataArrayItem.key;
      } else {
        item.question = '';
      }
    });
  };

  const updateNestedSection = (nestedSection) => {
    nestedSection.forEach((nestedItem) => {
      const titleKey = 'name';
      if (nestedItem && nestedItem[titleKey]) {
        const matchedDataArrayItem = dataFormat.find((data) => data.label === nestedItem[titleKey]);
        if (matchedDataArrayItem) {
          nestedItem.question = matchedDataArrayItem.key;
        } else {
          nestedItem.question = '';
        }
      }
    });
  };

  for (const section in updatedJsonData) {
    if (Array.isArray(updatedJsonData[section])) {
      updateSection(updatedJsonData[section]);
      updatedJsonData[section].forEach((obj) => {
        if (obj.question === 'Terms & Conditions' || Array.isArray(obj.nestedValue)) {
          updateNestedSection(obj.nestedValue);
        }
      });
    }
  }

  return updatedJsonData;
};

export const addLeadPartnerMemberRow = (data, otherKeyInfoWorksheet) => {
  const leadPartnerHeaders = ['Title', 'Name', 'Surname', 'Location', 'Email', 'Title', 'Role', 'Year of Experience', 'Hourly Rate'];
  const headerRow = otherKeyInfoWorksheet.addRow(leadPartnerHeaders);
  headerRow.font = { bold: true, color: { argb: 'FF000000' } };
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFFFFF' }
    };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center'
    };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' }
    };
  });
  const dataRowValues = [data.header, data.name, data.surname, data.location, data.email, data.title, data.role, data.year_of_experience, data.hourly_rate];
  const dataRow = otherKeyInfoWorksheet.addRow(dataRowValues);
  dataRow.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
  dataRow.eachCell((cell) => {
    cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
  });
};

export const addFileRow = (workbook, rowIndex, fileName) => {
  const fileRow = workbook.getRow(rowIndex);
  fileRow.getCell(1).value = 'Related Files';
  fileRow.getCell(2).value = fileName;
  fileRow.height = 20;
  fileRow.getCell(1).font = { bold: true };

  fileRow.eachCell((cell) => {
    cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
  });

  fileRow.commit();
  return rowIndex;
};
export const addVideoForOtherKeyInfoRow = (workbook, rowIndex, aditionalInfoValue) => {
  const fileRow = workbook.getRow(rowIndex);
  fileRow.getCell(1).value = 'Video files';
  fileRow.getCell(2).value = aditionalInfoValue;
  fileRow.height = 20;
  fileRow.getCell(1).font = { bold: true };

  fileRow.eachCell((cell) => {
    cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'center' };
  });

  fileRow.commit();
  return rowIndex;
};

export const addAdditionalInformationRow = (worksheet, additionalInfoValue) => {
  const titleRow = worksheet.addRow(['Title', 'Comment']);
  titleRow.eachCell((cell) => {
    cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
    cell.font = { bold: true };
  });

  const headerRow = worksheet.addRow(['Additional Information', additionalInfoValue]);
  headerRow.getCell(2).value = additionalInfoValue;
  headerRow.eachCell((cell, colNumber) => {
    cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
    cell.font = { bold: colNumber === 1 };
  });

  worksheet.columns.forEach((column) => {
    column.width = 20;
    column.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
  });

  worksheet.eachRow({ includeEmpty: false }, function (row) {
    row.eachCell({ includeEmpty: false }, function (cell) {
      cell.alignment = { wrapText: true, vertical: 'middle', horizontal: 'left' };
    });
  });

  return worksheet.lastRow.number + 1;
};

export const estimateFeeScenario = (data) => {
  const headers = ['Estimate', 'Fixed Fee', 'Capped Fee', 'Total Fee'];
  return headers.includes(data.header);
};
export const isFirstScenario = (data) => {
  const headers = ['Blended Fee', 'Contingent Fee', 'Abort Discount', 'Success Fee', 'Free initial advice up to a certain amount'];
  return headers.includes(data.header);
};
export const isSecondScenario = (data) => {
  const headers = [
    'Reverse Auction (To be conducted by Corporate outside of Zaven)',
    'Retainer (general/project based)',
    'Pricing Other',
    'Bidders to indicate any applicable tax to fees and %'
  ];
  return headers.includes(data.header);
};
// export function formatCredentials(htmlString) {
//   const parser = new DOMParser();
//   const doc = parser.parseFromString(htmlString, 'text/html');

//   const strongTags = doc.querySelectorAll('strong');
//   const credentials = Array.from(strongTags).map((tag) => tag.textContent.trim());

//   return credentials.join(',');
// }

export function formatCredentials(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  const elements = doc.querySelectorAll('strong, em, li, p, span, div');

  let credentials = [];

  elements.forEach((element) => {
    if (element.nodeName === 'LI') {
      const text = element.textContent.trim();
      if (text.match(/^\d/)) {
        credentials.push(text);
      }
    } else {
      const text = element.textContent.trim();
      if (text) {
        credentials.push(text);
      }
    }
  });

  return credentials.join(',');
}

export const compareStrings = (str1, str2) => {
  const normalizeString = (str) => str?.replace(/\s/g, '').toLowerCase();
  return normalizeString(str1) === normalizeString(str2);
};

export const expandByChildPath = (proposalValues, displayFlags, path, displayKey) => {
  let valueAtPath = path.split('.').reduce((obj, key) => (obj && key in obj ? obj[key] : null), proposalValues);
  displayFlags[displayKey] = !!valueAtPath && valueAtPath.trim() !== '';
};

export const convertQuestionToPath = (question) => {
  switch (question) {
    case 'structuring':
      return 'phase_one.structuring';
    case 'term_sheet':
      return 'phase_one.term_sheet';
    case 'marketing':
      return 'phase_two.marketing';
    case 'reviewing_pitch_book':
      return 'phase_two.marketing.reviewing_pitch_book';
    case 'marketing_advice':
      return 'phase_two.marketing.marketing_advice';
    case 'fund_interests':
      return 'phase_two.marketing.marketing_advice.fund_interests';
    case 'non_us_eu_jurisdictions':
      return 'phase_two.marketing.marketing_advice.non_us_eu_jurisdictions';
    case 'marketing_in_us':
      return 'phase_two.marketing.marketing_advice.marketing_in_us';
    case 'private_placement_exemptions':
      return 'phase_two.marketing.marketing_advice.marketing_in_us.private_placement_exemptions';
    case 'us_regulation_filings':
      return 'phase_two.marketing.marketing_advice.marketing_in_us.us_regulation_filings';
    case 'us_securities_law':
      return 'phase_two.marketing.marketing_advice.marketing_in_us.us_securities_law';
    case 'drafting_ppm':
      return 'phase_three.drafting_ppm';
    case 'core_documents':
      return 'phase_three.core_documents';
    case 'ancilliary_documents':
      return 'phase_three.ancilliary_documents';
    case 'entity_establishment':
      return 'phase_four.entity_establishment';
    case 'negotiations':
      return 'phase_four.negotiations';
    case 'closings':
      return 'phase_four.closings';
    case 'post_closing':
      return 'phase_five.post_closing';
    case 'optional_workstreams':
      return 'phase_five.optional_workstreams';
    default:
      return question;
  }
};
export const updateFundFormationObject = (fundFormationObject, templateItem) => {
  function setValueByPath(obj, path, acceptance, comment) {
    const keys = path.split('.');
    let current = obj;
    for (let i = 0; i < keys.length - 1; i++) {
      if (current[keys[i]] === undefined) {
        return;
      }
      current = current[keys[i]];
    }
    const lastKey = keys[keys.length - 1];
    if (current[lastKey] !== undefined) {
      // Check if the target property exists
      current[lastKey].noted_accepted = acceptance;
      current[lastKey].comment = comment;
    }
  }

  if (typeof templateItem === 'object' && templateItem !== null) {
    Object.entries(templateItem).forEach(() => {
      const path = convertQuestionToPath(templateItem.question);
      setValueByPath(fundFormationObject, path, templateItem.acceptance, templateItem.comment);
    });
  } else {
    console.error('templateItem is not an object or is null');
  }
  if (templateItem.nestedValue && templateItem.nestedValue.length > 0) {
    templateItem.nestedValue.forEach((nestedItem) => {
      const path = convertQuestionToPath(nestedItem.question);
      setValueByPath(fundFormationObject, path, nestedItem.acceptance, nestedItem.comment);
    });
    if (typeof templateItem.nestedValue === 'object' && templateItem.nestedValue !== null) {
      Object.entries(templateItem.nestedValue).forEach(() => {
        const path = convertQuestionToPath(templateItem.question);
        setValueByPath(fundFormationObject, path, templateItem.acceptance, templateItem.comment);
      });
    }
  }
};

export const validateAllFileFormat = async (fileObj) => {
  if (!fileObj) {
    return 'No file selected.';
  }
  if (fileObj?.size > 5 * 1024 * 1024) {
    return 'Maximum file size is 5mb';
  }
  // if (fileObj?.type === 'application/pdf') {
  //   const errorCheck: any = await maliciousPDFCheck(fileObj);
  //   if (errorCheck.containsScript || errorCheck.containsPotentiallyHarmful || errorCheck.containsAnnotationError) {
  //     return 'The file contains malicious content.';
  //   }
  // }
  if (!allowedFileTypes?.includes(fileObj?.type)) {
    return 'Invalid file type. Please upload a file of type: PDF, PNG, JPEG, Word, Excel, PowerPoint, Plain Text, or MP4.';
  }
  return null;
};

export const validateSpecificImageFormat = async (fileObj) => {
  if (!fileObj) {
    return 'No Image selected.';
  }
  const firstDotIndex = fileObj.name.indexOf('.');
  const remainingName = fileObj.name.substring(firstDotIndex + 1);
  if (remainingName.includes('.')) {
    return 'Invalid Image type. Please upload a Image of type: JPG, JPEG, PNG.';
  }
  if (fileObj.size / 1024 > 500) {
    return 'Maximum file size is 500kb';
  }
  if (!allowedImgTypes.includes(fileObj.type)) {
    return 'Only JPEG, JPG, and PNG file formats are allowed.';
  }
  return null;
};

export const validateSpecificFileFormat = async (fileObj) => {
  if (!fileObj) {
    return 'No file selected.';
  }
  if (fileObj.size > 5 * 1024 * 1024) {
    return 'Maximum file size is 5mb';
  }
  // if (fileObj.type === 'application/pdf') {
  //   const errorCheck: any = await maliciousPDFCheck(fileObj);
  //   if (errorCheck.containsScript || errorCheck.containsPotentiallyHarmful || errorCheck.containsAnnotationError) {
  //     return 'The file contains malicious content.';
  //   }
  // }
  if (!allowedDocsTypes.includes(fileObj.type)) {
    return 'Only PDF and DOCS file formats are allowed.';
  }
  return null;
};

export const checkNestedValues = (obj) => {
  if (!obj || typeof obj !== 'object') return false;

  if (obj.hasOwnProperty('value') && obj.value) return true;

  for (let key in obj) {
    if (checkNestedValues(obj[key])) return true;
  }

  return false;
};

export const getLogoUrl = (lawFirmName, proposalData) => {
  if (!lawFirmName || !proposalData) return DummyCompanyLogo;

  const matchedProposal = proposalData.find((item) => item.legal_service_detail?.[0]?.ls_provider_name === lawFirmName);

  return matchedProposal?.legal_service_detail?.[0]?.logo || DummyCompanyLogo;
};

// True when getLogoUrl fell back to the placeholder (no real company logo) — used to grey it out.
export const isDummyCompanyLogo = (url: string) => url === DummyCompanyLogo;

export const filteredStepListCheckBox = (stepListCheckBox, selectedFormSubmit) => {
  if (selectedFormSubmit && typeof selectedFormSubmit === 'object') {
    return stepListCheckBox.filter((step) => !selectedFormSubmit[step.code]);
  }
  return stepListCheckBox;
};

// react-draggable v4 types incorrectly mark DraggableCoreProps as required via static defaultProps.
// Typed as any to bypass the broken type check without affecting runtime behavior.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DraggableComponent: any = Draggable;

export default function PaperComponent(props: PaperProps) {
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <DraggableComponent nodeRef={nodeRef} handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'} bounds="body">
      <Paper
        {...props}
        ref={nodeRef}
        style={{
          pointerEvents: 'auto',
          overflow: 'hidden'
        }}
      />
    </DraggableComponent>
  );
}

export const highlighter = (input) => {
  const tagRegex = /<([^>]+)>/g;

  const sectionRegexes = [
    {
      title: '',
      regex:
        /(You are developing a recommendation engine.*?focusing on selecting one winning bidder while giving reasons for the rejection of other bidders\.)/gs
    },
    {
      title: '',
      regex: /(Given the following metrics from multiple law firms.*?process\.)/gs
    },
    { title: 'Dynamic Input Parameters:', regex: /Dynamic Input Parameters:(.*?)two key reasons for the decision\./gs },
    {
      title: '',
      regex: /(Output Format:.*?two key reasons for the decision\.)/gs
    },
    { title: '1. Pricing:', regex: /1\. Pricing:(.*?)Legal services provider 1 value:/gs },
    { title: '2. Assumptions & Exclusions:', regex: /2\. Assumptions & Exclusions:(.*?)Legal services provider 1 value:/gs },
    { title: '3. Key Team Members:', regex: /3\. Key Team Members:(.*?)Legal services provider 1 value:/gs },
    { title: '4. Staffing:', regex: /4\. Staffing:(.*?)Legal Services Provider 1 Value:/gs },
    { title: '5. Diversity:', regex: /5\. Diversity:(.*?)Legal services provider 1 value:/gs },
    { title: '6. Credentials:', regex: /6\. Credentials:(.*?)Legal services provider 1 value:/gs },
    { title: '7. Disaggregation:', regex: /7\. Disaggregation:(.*?)Legal services provider 1 value:/gs },
    {
      title: '8. USP (What is your USP (max 600 characters) ?):',
      regex: /8\. USP \(What is your USP \(max 600 characters\) \?\):(.*?)Legal services provider 1 value:/gs
    },
    { title: '9. Video:', regex: /9\. Video:(.*?)Legal services provider 1 value:/gs },
    { title: '10. Other:', regex: /10\. Other:(.*?)Legal services provider 1 value:/gs }
  ];

  let highlightedInput = input?.replace(
    tagRegex,
    '<span style="color: black; background-color: #FFFF04; user-select: none; -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; pointer-events: none;" title="Non-editable - LOCKED" contenteditable="false">&lt;$1&gt;</span>'
  );

  sectionRegexes.forEach(({ title, regex }) => {
    highlightedInput = highlightedInput?.replace(regex, (match, content) => {
      if (title === 'Dynamic Input Parameters:') {
        return `Dynamic Input Parameters:<span style="color: black; background-color: #bfd8b5;" title="Editable">${content}</span>two key reasons for the decision.`;
      }
      if (title === '') {
        return `<span style="color: black; background-color: #bfd8b5;" title="Editable">${content}</span>`;
      }
      return `${title}<span style="color: black; background-color: #bfd8b5;" title="Editable">${content}</span>Legal services provider 1 value:`;
    });
  });

  return highlightedInput;
};

let promptArray = [];
export const updatePromptArray = (selectedId, prompt) => {
  const existingItemIndex = promptArray.findIndex((item) => item.selectedId === selectedId);
  if (existingItemIndex !== -1) {
    promptArray[existingItemIndex] = { selectedId, prompt };
  } else {
    promptArray.push({ selectedId, prompt });
  }
};

export const getPromptArray: any = () => {
  return promptArray.map((item) => item);
};

export const getNewProposalScore = (rfpScoreData = []) => {
  return (rfpScoreData || []).map((item) => {
    const scoreCard = Array.isArray(item.scoring_card) && item.scoring_card.length > 0 ? item.scoring_card[0] : {};
    return {
      pricing: scoreCard.pricing?.score ?? '',
      assumptionsExclusions: scoreCard.assumptions_exclusions?.score ?? '',
      keyTeamMembers: scoreCard.key_team_members?.score ?? '',
      staffing: scoreCard.staffing?.score ?? '',
      diversity: scoreCard.diversity?.score ?? '',
      credentials: scoreCard.credentials?.score ?? '',
      disaggregation: scoreCard.disaggregation?.score ?? '',
      usp: scoreCard.usp?.score ?? '',
      video: scoreCard.video?.score ?? '',
      other: scoreCard.other?.score ?? ''
    };
  });
};

export const normalizeText = (str) => {
  return str?.toLowerCase().replace(/[\s()]/g, '');
};

export const returnSelectedScopeOfWork = (selectedValue, parentValue) => {
  let formGroups;
  let compName;
  switch (selectedValue) {
    case 'commercial_contracts':
      formGroups = `commercial_contracts.${parentValue}.scope_of_work[0].commercial_contracts`;
      compName = 'scopeCommercialFile';
      break;
    case 'competition':
      formGroups = `competition.${parentValue}.scope_of_work[0].competition`;
      compName = 'scopeCompetitionFile';
      break;
    case 'corporate_ma':
      formGroups = `corporate_ma.${parentValue}.scope_of_work[0].corporate_ma`;
      compName = 'scopeCorporateFile';
      break;
    case 'data_protection_privacy':
      formGroups = `data_protection_privacy.${parentValue}.scope_of_work[0].data_protection_privacy`;
      compName = 'scopeDataProtectionFile';
      break;
    case 'employment':
      formGroups = `employment.${parentValue}.scope_of_work[0].employment`;
      compName = 'scopeEmploymentFile';
      break;
    case 'financing_capital_markets':
      formGroups = `financing_capital_markets.${parentValue}.scope_of_work[0].financing_capital_markets`;
      compName = 'scopeFinancingFile';
      break;
    case 'infrastructure_projects_financing':
      formGroups = `infrastructure_projects_financing.${parentValue}.scope_of_work[0].infrastructure_projects_financing`;
      compName = 'scopeProjectsFile';
      break;
    case 'fund_formation':
      formGroups = `fund_formation.${parentValue}.scope_of_work[0].fund_formation`;
      compName = 'scopeFundFormationFile';
      break;
    case 'fund_investment':
      formGroups = `fund_investment.${parentValue}.scope_of_work[0].fund_investment`;
      compName = 'scopeFundInvestmentFile';
      break;
    case 'ip':
      formGroups = `ip.${parentValue}.scope_of_work[0].ip`;
      compName = 'scopeIpFile';
      break;
    case 'it':
      formGroups = `it.${parentValue}.scope_of_work[0].it`;
      compName = 'scopeItFile';
      break;
    case 'litigation':
      formGroups = `litigation.${parentValue}.scope_of_work[0].litigation`;
      compName = 'scopeLitigationFile';
      break;
    case 'arbitration':
      formGroups = `arbitration.${parentValue}.scope_of_work[0].arbitration`;
      compName = 'scopeArbitrationFile';
      break;
    case 'restructuring':
      formGroups = `restructuring.${parentValue}.scope_of_work[0].restructuring`;
      compName = 'scopeRestructuringFile';
      break;
    case 'insolvency':
      formGroups = `insolvency.${parentValue}.scope_of_work[0].insolvency`;
      compName = 'scopeInsolvencyFile';
      break;
    case 'regulatory':
      formGroups = `regulatory.${parentValue}.scope_of_work[0].regulatory`;
      compName = 'scopeRegulatoryFile';
      break;
    case 'tax':
      formGroups = `tax.${parentValue}.scope_of_work[0].tax`;
      compName = 'scopeTaxFile';
      break;
    case 'other':
      formGroups = `other.${parentValue}.scope_of_work[0].other`;
      compName = 'scopeOtherFile';
      break;
  }
  return { formGroups, compName };
};

export const UnsavedChangesHandler = ({ dirty, isSubmitted }: { dirty: boolean; isSubmitted: boolean }) => {
  // Check if proposal is being submitted to bypass the warning (reactive via custom event)
  const [isProposalSubmitting, setIsProposalSubmitting] = useState(() => sessionStorage.getItem('proposal_submitting') === 'true');

  // Listen for proposal_submitting_changed events dispatched from SubmitParticipationModel
  useEffect(() => {
    const handleProposalSubmittingChanged = () => {
      setIsProposalSubmitting(sessionStorage.getItem('proposal_submitting') === 'true');
    };
    window.addEventListener('proposal_submitting_changed', handleProposalSubmittingChanged);
    return () => window.removeEventListener('proposal_submitting_changed', handleProposalSubmittingChanged);
  }, []);

  usePrompt(
    'Your data will be lost if you leave this page. Click OK to confirm or Cancel to stay.',
    dirty && !isSubmitted && !isProposalSubmitting,
    (nextLocation) => {
      const { pathname } = nextLocation;
      // Allow internal RFP and Proposal creation steps
      if (
        pathname.startsWith('/corporate/rfp/create') ||
        pathname.startsWith('/legal-service/proposals') ||
        pathname.startsWith('/legal-service/master-template')
      )
        return false;

      // Allow navigation to Template Bank, Past RFPs, and Home (Save Draft)
      const whitelist = [
        '/corporate/template-bank/select-list',
        '/corporate/template-bank/list',
        '/corporate/past-rfp/list',
        '/',
        '/legal-service/past-proposal/select-list',
        '/legal-service/template-bank/select-list'
      ];
      if (whitelist.includes(pathname)) return false;

      return true;
    }
  );

  // Cleanup the flag after state update so usePrompt hooks have already re-evaluated
  useEffect(() => {
    if (isProposalSubmitting) {
      sessionStorage.removeItem('proposal_submitting');
    }
  }, [isProposalSubmitting]);

  return null;
};

// Extract all protected placeholder fields from the original prompt
export const extractProtectedFields = (input: string): string[] => {
  const tagRegex = /<([^>]+)>/g;
  const matches: string[] = [];
  let match;
  // eslint-disable-next-line no-cond-assign
  while ((match = tagRegex.exec(input)) !== null) {
    matches.push(`<${match[1]}>`);
  }

  return matches;
};

// Validate that all protected fields still exist in the edited prompt
export const validateProtectedFields = (
  originalPrompt: string,
  editedPrompt: string
): {
  isValid: boolean;
  missingFields: string[];
} => {
  const protectedFields = extractProtectedFields(originalPrompt);
  const missingFields: string[] = [];

  protectedFields.forEach((field) => {
    // Create multiple regex patterns to catch the field in different contexts
    const plainTextRegex = new RegExp(field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    const htmlEncoded = field.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const htmlEncodedRegex = new RegExp(htmlEncoded.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    const contenteditableRegex = new RegExp(`<span[^>]*contenteditable="false"[^>]*>${field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}</span>`, 'g');

    const found = plainTextRegex.test(editedPrompt) || htmlEncodedRegex.test(editedPrompt) || contenteditableRegex.test(editedPrompt);

    if (!found) {
      missingFields.push(field);
    }
  });

  return {
    isValid: missingFields.length === 0,
    missingFields
  };
};

// Restore missing protected fields to their original positions
export const restoreProtectedFields = (originalPrompt: string, editedPrompt: string): string => {
  const validation = validateProtectedFields(originalPrompt, editedPrompt);

  if (validation.isValid) {
    return editedPrompt;
  }

  let restoredPrompt = editedPrompt;

  validation.missingFields.forEach((field) => {
    // Find the field in the original prompt and get the highlighted version
    const styledSpanRegex = new RegExp(
      // eslint-disable-next-line no-useless-escape
      `<span[^>]*style="[^"]*background-color: #FFFF04[^"]*"[^>]*contenteditable="false"[^>]*>${field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}</span>`,
      'g'
    );

    const originalMatch = originalPrompt.match(styledSpanRegex);

    if (originalMatch && originalMatch.length > 0) {
      // Append the restored field to the end
      restoredPrompt += originalMatch[0];
    } else {
      // Fallback: try to create a protected span
      const safeField = field.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const fallbackSpan = `<span style="color: black; background-color: #FFFF04;" title="Non-editable" contenteditable="false">${field}</span>`;
      restoredPrompt += fallbackSpan;
    }
  });

  return restoredPrompt;
};

// Extract plain text from edited HTML content while preserving structure
export const extractPlainTextFromEditor = (htmlContent: string): string => {
  const temp = document.createElement('div');
  temp.innerHTML = htmlContent;

  // Get all text nodes while preserving the structure
  let text = '';
  const walker = document.createTreeWalker(temp, NodeFilter.SHOW_TEXT, null);

  let node;
  // eslint-disable-next-line no-cond-assign
  while ((node = walker.nextNode())) {
    text += node.textContent;
  }

  return text;
};
